'use client';

import { useEffect, useState } from 'react';
import { Modal } from '@/components/ui/Modal';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { cn } from '@/lib/cn';

interface ResponsiveModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose?: () => void;
  /** Modal title */
  label: string;
  /** Optional subtitle rendered inside the header, below `label`. */
  subtitle?: string;
  /** Maximum width for desktop modal */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  /** Whether the modal can be dismissed by clicking overlay or pressing ESC */
  dismissible?: boolean;
  /** Click handler to show a default back icon button on the left */
  onBack?: () => void;
  /** When true, the mobile BottomSheet expands to full viewport height */
  mobileFullHeight?: boolean;
  /** Children content */
  children: React.ReactNode;
  /** Whether to close the modal when the overlay is clicked */
  closeOnOverlayClick?: boolean;
  /** Whether to show the drag handle on mobile (default: true) */
  showHandle?: boolean;
  /** Whether dragging is only allowed from the handle (default: true) */
  handleOnly?: boolean;
  /**
   * Set true when this modal is opened from *inside another* BottomSheet
   * / Modal. On mobile it bumps the overlay + content z-index so the
   * dark backdrop actually covers the parent sheet (default BottomSheet
   * z-index matches the parent, which leaves the parent's title strip
   * visible above the child). No effect on desktop.
   */
  nested?: boolean;
  /**
   * Extra classes appended to the desktop Modal's outer container
   * (e.g. `rounded-4xl` to override the default `rounded-2xl` shell).
   * Ignored on mobile since the mobile BottomSheet has its own
   * top-radius handled by Vaul.
   */
  contentClassName?: string;
  /**
   * Extra classes appended to the Modal's INNER body wrapper on
   * desktop. Used when the outer `contentClassName` overrides the
   * radius — without a matching body radius the inner corners show
   * through as a "double-rim" bottom (the body's own `rounded-2xl`
   * clips inside the outer 32px shell). Ignored on mobile.
   */
  desktopBodyClassName?: string;
  /**
   * Extra classes appended to the outer container on BOTH desktop
   * (Modal) and mobile (BottomSheet). Use for cross-platform tweaks
   * like hiding the AppBar's built-in divider via a scoped selector:
   *   `[&_[role=banner]]:!border-b-0`
   * `contentClassName` targets desktop only; this one covers both.
   */
  crossPlatformClassName?: string;
  /**
   * REPLACES the padding on the shared body wrapper (default `py-4 px-6`).
   *
   * It replaces rather than appends because `cn` here is a plain join, not
   * tailwind-merge: appending `px-4` next to `px-6` emits both and lets CSS
   * source order pick the winner. Pass the full padding you want.
   */
  contentPaddingClassName?: string;
}

/**
 * Responsive modal that renders as Modal on desktop and BottomSheet on mobile
 */
export function ResponsiveModal({
  open,
  onClose,
  label,
  subtitle,
  maxWidth = 'md',
  dismissible = true,
  onBack,
  mobileFullHeight = false,
  children,
  closeOnOverlayClick = true,
  showHandle = true,
  handleOnly = true,
  nested = false,
  contentClassName,
  desktopBodyClassName,
  crossPlatformClassName,
  contentPaddingClassName,
}: ResponsiveModalProps) {
  // Important: initialize from window to avoid a "desktop modal flash" before BottomSheet renders.
  // This happens when the modal component is mounted only on click (e.g. complaints month modal).
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768; // md breakpoint
  });

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  const content = (
    <div
      className={cn(
        'bg-primary overflow-y-auto hide-scrollbar',
        contentPaddingClassName ?? 'py-4 px-6',
        // When we take the full mobile sheet, give callers a proper
        // flex column so they can pin a footer to the bottom (step
        // body flex-1, footer sibling at end). Without this the
        // wrapper is auto-height and the child's `h-full` collapses
        // to zero.
        mobileFullHeight && isMobile && 'h-full flex flex-col',
      )}
    >
      {children}
    </div>
  );

  if (isMobile) {
    // When opened from inside another sheet/modal (nested=true), bump
    // the z-index so the built-in dark overlay actually covers the
    // parent sheet — otherwise the parent's title strip pokes through
    // above the child. Values are one tier above BottomSheet's stock
    // z-40 overlay / z-50 content.
    return (
      <BottomSheet
        open={open}
        onOpenChange={isOpen => !isOpen && onClose?.()}
        label={label}
        {...(subtitle !== undefined ? { subtitle } : {})}
        bodyClassName="!p-0"
        leading={false}
        {...(mobileFullHeight ? { fullHeight: true } : {})}
        // When the caller supplies `onBack`, treat mobile as a
        // "back-arrow only" page view (matches the mweb "new page"
        // pattern) — pass `onBack` but suppress the AppBar's close-X
        // so the header has a single leading affordance. The drawer
        // still dismisses via backdrop/Esc/swipe via `onOpenChange`
        // above. Desktop keeps its close-X because the Modal branch
        // below always forwards `onClose`.
        {...(onBack === undefined && onClose !== undefined && { onClose })}
        {...(onBack !== undefined && { onBack })}
        dismissible={dismissible}
        closeOnOverlayClick={closeOnOverlayClick}
        showHandle={showHandle}
        handleOnly={handleOnly}
        {...(nested
          ? {
              overlayClassName: '!z-[60]',
              contentClassName: cn('!z-[70]', crossPlatformClassName),
            }
          : crossPlatformClassName
            ? { contentClassName: crossPlatformClassName }
            : {})}
      >
        {content}
      </BottomSheet>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={isOpen => !isOpen && onClose?.()}
      // Desktop keeps the close-X only. When callers pass `onBack`
      // it's a mobile-only "new page" affordance (see BottomSheet
      // branch above) — showing it on desktop next to the X would
      // be redundant, so we suppress it here.
      {...(onClose !== undefined && { onClose })}
      label={label}
      {...(subtitle !== undefined ? { subtitle } : {})}
      maxWidth={maxWidth}
      dismissible={dismissible}
      // Body radius is passed through so callers overriding the
      // outer container radius (`contentClassName`) can keep the
      // inner body in lockstep. Without matching radii the inner
      // body's `rounded-2xl` visibly clips inside the outer shell
      // and paints as a "double-rim" bottom edge.
      bodyClassName={cn('!p-0 overflow-hidden', desktopBodyClassName ?? 'rounded-2xl')}
      closeOnOverlayClick={closeOnOverlayClick}
      {...(contentClassName || crossPlatformClassName
        ? { contentClassName: cn(contentClassName, crossPlatformClassName) }
        : {})}
    >
      {content}
    </Modal>
  );
}
